/**
 * Router Component
 * Main router configuration and setup
 */

import { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ProtectedRoute, RoleBasedRoute, PublicRoute } from '@/common/auth';
import { RootLayout } from '@/common/layouts';
import LoadingScreen from '@/common/components/LoadingScreen';
import { usePushSubscription } from '@/modules/notifications';
import {
  PUBLIC_ROUTES,
  AUTH_ROUTES,
  ADMIN_ROUTES,
  ERROR_ROUTES,
} from './routes';
import type { RouteConfig } from './routes';

// Standalone public landing for placeholder-invite emails. Rendered outside
// the PublicRoute/RootLayout wrappers so invited guests (logged in or not) hit
// it directly instead of being bounced to the dashboard or shown the sidebar.
const ClaimInvitePage = lazy(() =>
  import('@/pages/auth/ClaimInvitePage').then((m) => ({ default: m.ClaimInvitePage })),
);

/**
 * Loading fallback component
 */
function LoadingFallback() {
  return <LoadingScreen />;
}

/**
 * Route wrapper component
 */
interface RouteWrapperProps {
  route: RouteConfig;
}

function RouteWrapper({ route }: RouteWrapperProps) {
  const routeContent = <Suspense fallback={<LoadingFallback />}>{route.element}</Suspense>;

  // Don't wrap auth pages or callback pages with header/layout
  const wrappedContent =
    route.path !== '/login' ? (
      <RootLayout showSidebar={!route.skipLayout}>{routeContent}</RootLayout>
    ) : (
      routeContent
    );

  // Public routes
  if (!route.requiresAuth) {
    return <PublicRoute redirectTo="/dashboard">{wrappedContent}</PublicRoute>;
  }

  // Protected routes
  if (!route.requiredRoles) {
    return <ProtectedRoute>{wrappedContent}</ProtectedRoute>;
  }

  // Role-based routes
  return <RoleBasedRoute requiredRoles={route.requiredRoles}>{wrappedContent}</RoleBasedRoute>;
}

/**
 * Main Router component
 */
function PushSubscriptionRegistrar() {
  usePushSubscription();
  return null;
}

export function Router() {
  return (
    <BrowserRouter>
      <PushSubscriptionRegistrar />
      <Routes>
        {/* Invite claim landing — standalone, no auth guard or layout */}
        <Route
          path="/claim"
          element={
            <Suspense fallback={<LoadingFallback />}>
              <ClaimInvitePage />
            </Suspense>
          }
        />

        {/* Public routes */}
        {PUBLIC_ROUTES.map((route) => (
          <Route key={route.path} path={route.path} element={<RouteWrapper route={route} />} />
        ))}

        {/* Auth routes */}
        {AUTH_ROUTES.map((route) => (
          <Route key={route.path} path={route.path} element={<RouteWrapper route={route} />} />
        ))}

        {/* Admin routes */}
        {ADMIN_ROUTES.map((route) => (
          <Route key={route.path} path={route.path} element={<RouteWrapper route={route} />} />
        ))}

        {/* Error routes - must be last */}
        {ERROR_ROUTES.map((route) => (
          <Route key={route.path} path={route.path} element={<RouteWrapper route={route} />} />
        ))}

        {/* Fallback redirect */}
        <Route path="*" element={<Navigate to="/404" replace />} />
      </Routes>
    </BrowserRouter>
  );
}
